/**
 * SchedulePage — the flagship day-schedule for the med-spa (Boulevard
 * replacement). An appointment is a VISIT: one or more back-to-back service
 * segments, each with its own provider and room. The day grid therefore draws
 * one block per segment — a client who starts in the Consult Lounge with the
 * provider and finishes in a drip chair with the nurse shows in both columns,
 * with the blocks visually linked. Week view stays one chip per visit.
 * Booking, rescheduling and blocking time all go through the open-slots
 * engine so double-booking is impossible from this screen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Ban,
  CalendarDays,
  CalendarRange,
  CalendarX2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Clock,
  DoorOpen,
  Plus,
  Search,
  User,
  Users,
  X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  bookingGridMinutes, displayDayKey, displayDayStart, displayMinutesOfDay,
  formatDate, formatMoney, formatPlainDate, formatTime, fullName,
} from "@/lib/format";
import {
  Badge,
  Button,
  DateTimePicker,
  Input,
  Label,
  Modal,
  ModalFooter,
  Select,
  Skeleton,
  EmptyState,
  useConfirm,
  useToast,
} from "@/components/ui";
import {
  AppointmentFormModal, DOT_CLASSES, formatDuration, visitSegments,
} from "@/features/scheduling/AppointmentFormModal";
import type { CalendarPick, FormMode } from "@/features/scheduling/AppointmentFormModal";
import { COLOR_CLASSES, TINT_CLASSES, visitColorClass } from "@/lib/seat-colors";
import { splitBlocks } from "@/lib/schedule-blocks";
import { reseatLines, reseatPlan, reseatSummary, type ReseatMove } from "@/lib/reseat";
import type {
  Appointment,
  AppointmentSegment,
  Contact,
  PackageBalance,
  Provider,
  Room,
  ScheduleBlock,
  Service,
} from "@/lib/types";

/* ─── Constants ────────────────────────────────────────────────────────────── */

const DAY_START_MIN = 8 * 60; // 8 AM
const DAY_END_MIN = 20 * 60; // 8 PM
// "on the calendar view if you go to Sept 30 it's hard for me to read the
// actual time of the visit can we add the 30 and 15 increments and darken
// the lines" — 80px an hour puts 20px between quarter-hour lines, which is
// the point at which a 15-minute visit reads as a band and not a hairline.
const HOUR_PX = 80;
const CANVAS_PX = ((DAY_END_MIN - DAY_START_MIN) / 60) * HOUR_PX;
const GUTTER_PX = 56; // 3.5rem
const COL_MIN_PX = 160;

/**
 * "Unconfirmed" vs "Confirmed" is the distinction the front desk works off
 * every morning, so a booked-but-unconfirmed visit says so in plain zinc and
 * a confirmed one reads blue.
 */
const STATUS_META: Record<Appointment["status"], { label: string; chip: string; badge: string }> = {
  scheduled: { label: "Unconfirmed", chip: "bg-zinc-700/80 text-zinc-300", badge: "zinc" },
  confirmed: { label: "Confirmed", chip: "bg-sky-500/20 text-sky-300", badge: "blue" },
  arrived: { label: "Arrived", chip: "bg-emerald-500/20 text-emerald-300", badge: "emerald" },
  completed: { label: "Done", chip: "bg-emerald-500/10 text-emerald-400/80", badge: "emerald" },
  cancelled: { label: "Cancelled", chip: "bg-rose-500/20 text-rose-300", badge: "rose" },
  no_show: { label: "No-show", chip: "bg-amber-500/20 text-amber-300", badge: "amber" },
};

const ACTIVE_STATUSES: Appointment["status"][] = ["scheduled", "confirmed", "arrived"];

/* ─── Date helpers ─────────────────────────────────────────────────────────── */

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function startOfWeek(d: Date): Date {
  const s = startOfDay(d);
  return addDays(s, -s.getDay()); // back to Sunday
}

/**
 * Wall-clock minutes for `iso` on the day being viewed. Anything that falls on
 * another date (a block spanning several days) is pushed far outside the
 * canvas so the caller's clamp trims it to the day's edge.
 */
/**
 * Where on the day canvas an instant sits, in minutes past midnight — read in
 * the PRACTICE's timezone, the same one the time labels are printed in. Off-day
 * instants are pushed far outside the canvas so they clamp out of view.
 */
function minutesInDay(iso: string, day: Date): number {
  const dayKey = displayDayKey(day);
  const d = new Date(iso);
  if (displayDayKey(d) === dayKey) return displayMinutesOfDay(d);
  return d.getTime() < displayDayStart(dayKey).getTime() ? -24 * 60 : 48 * 60;
}

function yFromMinutes(min: number): number {
  return ((min - DAY_START_MIN) / 60) * HOUR_PX;
}

function hourLabel(h: number): string {
  const suffix = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr} ${suffix}`;
}


/** `YYYY-MM-DDTHH:mm` (what DateTimePicker emits) → ISO, or null if unparseable. */
function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/* ─── Visit helpers ────────────────────────────────────────────────────────── */

/**
 * Segments in performed order. Older rows (and optimistic merges) may arrive
 * without them, so fall back to a synthetic single segment built from the
 * appointment's own fields — every caller can then treat a visit uniformly.
 */

/** "Consult + Hydrafacial" — every service in the visit, in order. */
function visitTitle(a: Appointment): string {
  const names = visitSegments(a)
    .map((s) => s.service_name)
    .filter((n): n is string => !!n);
  if (names.length > 0) return names.join(" + ");
  return a.service_name ?? a.title;
}

function visitTotalCents(a: Appointment): number | null {
  const segs = visitSegments(a);
  const addons = segs.reduce((sum, s) => sum + (s.addons ?? []).reduce((n, x) => n + x.price_cents, 0), 0);
  const priced = segs.filter((s) => s.price_cents != null);
  if (priced.length === 0) return a.price_cents ?? (addons > 0 ? addons : null);
  return priced.reduce((sum, s) => sum + (s.price_cents ?? 0), 0) + addons;
}

interface FormsMeta {
  total: number;
  completed: number;
  done: boolean;
  label: string;
  chip: string;
  pill: string;
}

/** Paperwork progress for the visit, or null when no forms were sent. */
function formsMeta(a: Appointment): FormsMeta | null {
  const total = a.forms_total;
  if (!total || total <= 0) return null;
  const completed = Math.min(Math.max(a.forms_completed ?? 0, 0), total);
  const done = completed >= total;
  return {
    total,
    completed,
    done,
    label: done ? "Forms ✓" : `Forms ${completed}/${total}`,
    chip: done ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300",
    pill: done
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
      : "border-amber-500/30 bg-amber-500/10 text-amber-300",
  };
}

function needsPaperwork(a: Appointment): boolean {
  const meta = formsMeta(a);
  return meta !== null && !meta.done;
}

/**
 * Merge a PATCH response into a list row without losing the joined display
 * fields (names/colors/segments) if the server response omits or nulls them.
 */
function mergeAppointment(prev: Appointment, next: Appointment): Appointment {
  return {
    ...prev,
    ...next,
    contact_name: next.contact_name ?? prev.contact_name,
    service_name: next.service_name ?? prev.service_name,
    service_color: next.service_color ?? prev.service_color,
    provider_name: next.provider_name ?? prev.provider_name,
    provider_color: next.provider_color ?? prev.provider_color,
    room_name: next.room_name ?? prev.room_name,
    room_color: next.room_color ?? prev.room_color,
    segments: next.segments && next.segments.length > 0 ? next.segments : prev.segments,
  };
}

/* ─── Page ─────────────────────────────────────────────────────────────────── */

type ViewMode = "day" | "week";
type GroupBy = "provider" | "room";


export function SchedulePage() {
  const toast = useToast();
  const confirm = useConfirm();

  const [cursor, setCursor] = useState<Date>(() => startOfDay(new Date()));
  const [view, setView] = useState<ViewMode>("day");
  // Seats first — her words: "it needs to be not provider but the chair."
  // The provider view stays one click away; the choice is remembered.
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    try {
      const saved = localStorage.getItem("schedule.groupBy");
      return saved === "provider" ? "provider" : "room";
    } catch { return "room"; }
  });
  useEffect(() => {
    try { localStorage.setItem("schedule.groupBy", groupBy); } catch { /* private mode */ }
  }, [groupBy]);
  const [paperworkOnly, setPaperworkOnly] = useState(false);

  const [providers, setProviders] = useState<Provider[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [refLoading, setRefLoading] = useState(true);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [blocks, setBlocks] = useState<ScheduleBlock[]>([]);
  const [apptsLoading, setApptsLoading] = useState(true);

  const [detail, setDetail] = useState<Appointment | null>(null);
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [blockOpen, setBlockOpen] = useState(false);
  /** A pick-up being created from a click in the pick-up column. */
  const [pickupAt, setPickupAt] = useState<string | null>(null);
  /* "Select time on calendar" — the dialog steps aside, she clicks the grid,
     and the spot she clicked comes back to it. */
  const [placing, setPlacing] = useState<{ label: string; minutes: number } | null>(null);
  const [calendarPick, setCalendarPick] = useState<CalendarPick | null>(null);
  useEffect(() => {
    if (!placing) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setPlacing(null); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [placing]);

  /* Reference data — once on mount. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, r, s] = await Promise.all([
          api.get<{ providers: Provider[] }>("/providers"),
          // Retired seats too: a past visit still shows the chair it was in.
          api.get<{ rooms: Room[] }>("/rooms", { all: 1 }),
          api.get<{ services: Service[] }>("/services"),
        ]);
        if (cancelled) return;
        setProviders(p.providers);
        setRooms(r.rooms);
        setServices(s.services);
      } catch (err) {
        if (!cancelled) toast.error(err instanceof ApiError ? err.message : "Failed to load schedule data");
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  /* Visible range — day: local [00:00, 24:00) of cursor; week: Sun..Sat. */
  const cursorTime = cursor.getTime();
  // Fetch on the practice's day boundaries, not the viewer's, so the grid is
  // handed exactly the visits it's about to draw.
  const range = useMemo(() => {
    const base = new Date(cursorTime);
    const first = view === "day" ? startOfDay(base) : startOfWeek(base);
    const span = view === "day" ? 1 : 7;
    return {
      from: displayDayStart(displayDayKey(first)),
      to: displayDayStart(displayDayKey(addDays(first, span))),
    };
  }, [cursorTime, view]);

  /**
   * Appointments and blocked time cover the same window. They're settled
   * independently so a failure on one doesn't blank the other.
   */
  const fetchSchedule = useCallback(async (silent: boolean) => {
    if (!silent) setApptsLoading(true);
    const params = { from: range.from.toISOString(), to: range.to.toISOString() };
    const [apptRes, blockRes] = await Promise.allSettled([
      api.get<{ appointments: Appointment[] }>("/appointments", params),
      api.get<{ blocks: ScheduleBlock[] }>("/blocks", params),
    ]);
    if (apptRes.status === "fulfilled") {
      setAppointments(apptRes.value.appointments);
    } else if (!silent) {
      const err = apptRes.reason;
      toast.error(err instanceof ApiError ? err.message : "Failed to load appointments");
    }
    if (blockRes.status === "fulfilled") {
      setBlocks(blockRes.value.blocks);
    } else if (!silent) {
      toast.error("Failed to load blocked time");
    }
    if (!silent) setApptsLoading(false);
  }, [range, toast]);

  useEffect(() => {
    void fetchSchedule(false);
  }, [fetchSchedule]);

  /* Poll every 60s so the front desk sees online bookings land. */
  useEffect(() => {
    const t = window.setInterval(() => {
      if (!document.hidden) void fetchSchedule(true);
    }, 60_000);
    return () => window.clearInterval(t);
  }, [fetchSchedule]);

  const applyUpdate = useCallback((updated: Appointment) => {
    setAppointments((prev) => prev.map((a) => (a.id === updated.id ? mergeAppointment(a, updated) : a)));
    setDetail((d) => (d && d.id === updated.id ? mergeAppointment(d, updated) : d));
  }, []);

  const handleDeleteBlock = useCallback(async (block: ScheduleBlock) => {
    const who = block.provider_name ?? block.room_name ?? "This column";
    const ok = await confirm({
      title: "Remove this blocked time?",
      description: `${who} · ${formatDate(block.starts_at, { month: "short", day: "numeric" })} ${formatTime(block.starts_at)} – ${formatTime(block.ends_at)} opens back up for booking.`,
      confirmLabel: "Remove block",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/blocks/${block.id}`);
      setBlocks((prev) => prev.filter((b) => b.id !== block.id));
      toast.success("Blocked time removed");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to remove blocked time");
    }
  }, [confirm, toast]);

  const step = (dir: -1 | 1) => {
    setCursor((c) => addDays(c, view === "day" ? dir : dir * 7));
  };

  /* Paperwork chase list — the toggle filters down to visits still waiting. */
  const chaseCount = useMemo(
    () => appointments.filter((a) => ACTIVE_STATUSES.includes(a.status) && needsPaperwork(a)).length,
    [appointments]
  );
  const visibleAppointments = useMemo(
    () => (paperworkOnly ? appointments.filter(needsPaperwork) : appointments),
    [appointments, paperworkOnly]
  );

  /**
   * A drag commits here. The rules keep multi-service visits sane:
   *   - vertical: the WHOLE visit shifts by the delta (a late client is late
   *     for all of it), every seat's hold moving with its own leg;
   *   - horizontal: only the DRAGGED leg changes seat/provider — a consult
   *     that then moves to a chair is two rooms on purpose.
   * The server conflict-checks the result; a clash bounces the card back
   * with the reason. The client's "text me updates" preference on the visit
   * decides whether they're told, same as the Change-visit dialog.
   */
  const handleDragMove = useCallback(async (
    placement: SegmentPlacement,
    change: { minutesDelta: number; targetColId: string | null },
    reseat = false,
  ): Promise<boolean> => {
    const a = placement.visit;
    const segs = visitSegments(a);
    const deltaMs = change.minutesDelta * 60_000;
    const segments = segs.map((sg) => ({
      service_id: sg.service_id,
      provider_id: groupBy === "provider" && change.targetColId && sg.id === placement.segment.id
        ? change.targetColId : sg.provider_id,
      room_id: groupBy === "room" && change.targetColId && sg.id === placement.segment.id
        ? change.targetColId : sg.room_id,
      starts_at: new Date(new Date(sg.starts_at).getTime() + deltaMs).toISOString(),
      ends_at: new Date(new Date(sg.ends_at).getTime() + deltaMs).toISOString(),
      price_cents: sg.price_cents,
    }));
    try {
      const saved = await api.patch<{ moved?: ReseatMove[] }>(`/appointments/${a.id}`, {
        starts_at: new Date(new Date(a.starts_at).getTime() + deltaMs).toISOString(),
        ends_at: new Date(new Date(a.ends_at).getTime() + deltaMs).toISOString(),
        segments,
        ...(reseat ? { reseat: true } : {}),
      });
      const shuffled = reseatSummary(saved.moved ?? []);
      const texted = change.minutesDelta !== 0 && a.notify_client !== false && !!a.contact_id;
      if (texted) {
        // Her ask: a tick to say send or not. One click holds the text that's
        // waiting; the visit keeps its "text the client" setting for next time.
        toast.success("Time updated", "The client gets one text with the new time in 2 minutes.", {
          label: "Don't text them",
          onClick: () => {
            void api.post(`/appointments/${a.id}/skip-notice`, {})
              .then(() => toast.info("No text will go out for this move"))
              .catch(() => toast.error("Couldn't hold the text"));
          },
        });
      } else {
        toast.success(change.minutesDelta === 0 ? "Moved to the new seat" : "Time updated", shuffled);
      }
      void fetchSchedule(false);
      return true;
    } catch (err) {
      /* The chair is taken, but another of the same sort is free for the
         whole of the other client's stay: offer the swap rather than just
         bouncing the card back. */
      if (err instanceof ApiError && err.status === 409 && !reseat) {
        const moves = reseatPlan(err.data);
        if (moves.length > 0) {
          const ok = await confirm({
            title: moves.length === 1 ? "Move the other client to a free chair?" : "Move the other clients to free chairs?",
            description: (
              <span className="space-y-2">
                <span className="block">{err.message}</span>
                {reseatLines(moves, formatTime).map((line) => (
                  <span key={line} className="block text-zinc-200">{line}</span>
                ))}
                <span className="block text-zinc-500">
                  Same time, same clinician, same treatment - only the chair changes, and they are not texted about it.
                </span>
              </span>
            ),
            confirmLabel: moves.length === 1 ? "Move them and save" : "Move them all and save",
          });
          if (ok) return await handleDragMove(placement, change, true);
        }
      }
      toast.error(err instanceof ApiError ? err.message : "Couldn't move the visit");
      void fetchSchedule(false);
      return false;
    }
  }, [groupBy, toast, confirm, fetchSchedule]);

  const todayKey = displayDayKey(new Date());
  const isViewingToday = displayDayKey(cursor) === todayKey;

  const dateLabel = view === "day"
    ? formatDate(cursor, { weekday: "short", month: "short", day: "numeric" })
    : `${formatDate(range.from, { month: "short", day: "numeric" })} – ${formatDate(addDays(range.to, -1), { month: "short", day: "numeric" })}`;

  const loading = refLoading || apptsLoading;

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-zinc-800/70 px-4 py-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-zinc-100">Schedule</h1>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Previous" onClick={() => step(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {/* Same as the front desk: the button says which day is on screen,
                and is still the way back to today. On a week view the range is
                what matters, so it keeps the plain word. */}
            <Button variant="outline" size="sm"
              title={isViewingToday ? undefined : "Back to today"}
              onClick={() => setCursor(startOfDay(new Date()))}>
              {view === "day" && !isViewingToday
                ? formatDate(cursor, { weekday: "long" })
                : "Today"}
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Next" onClick={() => step(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <span className={cn("text-sm font-medium", isViewingToday && view === "day" ? "text-indigo-300" : "text-zinc-300")}>
            {dateLabel}
          </span>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { value: "day", label: "Day" },
              { value: "week", label: "Week" },
            ]}
          />
          {view === "day" && (
            <Segmented
              value={groupBy}
              onChange={setGroupBy}
              options={[
                { value: "provider", label: "By provider" },
                { value: "room", label: "By room" },
              ]}
            />
          )}
          <Button
            variant="outline"
            size="sm"
            aria-pressed={paperworkOnly}
            leftIcon={<ClipboardList className="h-4 w-4" />}
            onClick={() => setPaperworkOnly((v) => !v)}
            className={cn(paperworkOnly && "border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/15")}
          >
            Needs paperwork
            {chaseCount > 0 && (
              <span className="ml-1.5 rounded bg-amber-500/20 px-1 text-[10px] font-semibold tabular-nums text-amber-200">
                {chaseCount}
              </span>
            )}
          </Button>
          <Button variant="outline" size="sm" leftIcon={<Ban className="h-4 w-4" />} onClick={() => setBlockOpen(true)}>
            Block time
          </Button>
          {/* "The button to the chat should be right off front desk and
              calendar view" — the panel opens over the day, so nobody has to
              leave the schedule to answer a question about it. */}
          <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setFormMode({ kind: "create" })}>
            New appointment
          </Button>
        </div>
      </div>

      {/* Placing a visit by hand — one line, and a way out. */}
      {placing && (
        <div
          data-placing-bar
          className="flex items-center gap-3 border-b border-indigo-500/40 bg-indigo-500/10 px-4 py-2 text-sm text-indigo-100"
        >
          <CalendarRange className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate">
            Click the day where <span className="font-semibold">{placing.label}</span> should go.
          </span>
          <Button variant="ghost" size="sm" onClick={() => setPlacing(null)}>Cancel</Button>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <ScheduleSkeleton />
        ) : view === "day" ? (
          <DayView
            appointments={visibleAppointments}
            blocks={blocks}
            providers={providers}
            rooms={rooms}
            groupBy={groupBy}
            day={range.from}
            isToday={isViewingToday}
            onSelect={setDetail}
            onDeleteBlock={handleDeleteBlock}
            onMove={handleDragMove}
            placing={placing}
            onPlace={(colId, start) => {
              setCalendarPick({
                token: Date.now(),
                start,
                date: displayDayKey(cursor),
                room_id: groupBy === "room" ? colId : null,
                provider_id: groupBy === "provider" ? colId : null,
              });
              setPlacing(null);
            }}
            onPickup={(start) => setPickupAt(start)}
          />
        ) : (
          <WeekView
            appointments={visibleAppointments}
            blocks={blocks}
            weekStart={range.from}
            todayKey={todayKey}
            onSelect={setDetail}
            onOpenDay={(d) => { setCursor(startOfDay(d)); setView("day"); }}
          />
        )}
      </div>

      {detail && (
        <AppointmentDetailModal
          appointment={detail}
          onClose={() => setDetail(null)}
          onUpdated={applyUpdate}
          onReschedule={(a) => { setDetail(null); setFormMode({ kind: "reschedule", appointment: a }); }}
        />
      )}

      {formMode && (
        <AppointmentFormModal
          mode={formMode}
          services={services}
          providers={providers}
          defaultDate={displayDayKey(cursor)}
          onClose={() => { setPlacing(null); setFormMode(null); }}
          onSaved={() => { setPlacing(null); setFormMode(null); void fetchSchedule(false); }}
          hidden={!!placing}
          calendarPick={calendarPick}
          onPickOnCalendar={({ date, label, minutes }) => {
            // Show her the day she's booking, by seat — the columns she
            // thinks in — then step out of the way.
            setView("day");
            setGroupBy("room");
            setCursor(displayDayStart(date));
            setPlacing({ label, minutes });
          }}
        />
      )}

      {blockOpen && (
        <BlockTimeModal
          providers={providers}
          rooms={rooms.filter((r) => r.active)}
          defaultDate={displayDayKey(cursor)}
          onClose={() => setBlockOpen(false)}
          onSaved={() => { setBlockOpen(false); void fetchSchedule(false); }}
        />
      )}

      {pickupAt && (
        <PickupModal
          startIso={pickupAt}
          onClose={() => setPickupAt(null)}
          onSaved={() => { setPickupAt(null); void fetchSchedule(false); }}
        />
      )}
    </div>
  );
}

/* ─── Segmented toggle ─────────────────────────────────────────────────────── */

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="inline-flex items-center rounded-lg border border-zinc-800 bg-zinc-900/50 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium",
            value === o.value ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ─── Loading skeleton ─────────────────────────────────────────────────────── */

function ScheduleSkeleton() {
  return (
    <div className="flex gap-px p-4">
      <div className="w-14 shrink-0" />
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex-1 space-y-3 px-2">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ))}
    </div>
  );
}

/* ─── Day view ─────────────────────────────────────────────────────────────── */

interface DayColumn {
  id: string | null;
  name: string;
  dot: string;
  /** Header wash, so a glance at the top of the grid says which seat is which. */
  tint: string;
  /** The raw colour name, so a visit in this column can be painted to match. */
  color: string | null;
  /**
   * The medication pick-up column, which behaves differently: clicking an
   * empty slot in it starts a pick-up rather than doing nothing.
   *
   * "is it possible to click in the medication pick up from the calendar view
   *  and once we click it it can say create pickup"
   */
  pickup?: boolean;
}

/** One segment placed in one column — the unit the day grid actually draws. */
interface SegmentPlacement {
  visit: Appointment;
  segment: AppointmentSegment;
  index: number;
  total: number;
}

function DayView({
  appointments,
  blocks,
  providers,
  rooms,
  groupBy,
  day,
  isToday,
  onSelect,
  onDeleteBlock,
  onMove,
  placing,
  onPlace,
  onPickup,
}: {
  appointments: Appointment[];
  blocks: ScheduleBlock[];
  providers: Provider[];
  rooms: Room[];
  groupBy: GroupBy;
  day: Date;
  isToday: boolean;
  onSelect: (a: Appointment) => void;
  onDeleteBlock: (b: ScheduleBlock) => void;
  onMove: (
    placement: SegmentPlacement,
    change: { minutesDelta: number; targetColId: string | null },
  ) => Promise<boolean>;
  /** Placing a visit by hand ("Select time on calendar"): the ghost that
   *  follows the pointer, and where she finally clicks. */
  placing?: { label: string; minutes: number } | null;
  onPlace?: (colId: string | null, start: string) => void;
  /** Clicking a free slot in the pick-up column, with the time clicked. */
  onPickup?: (start: string) => void;
}) {
  /* "Now" line — re-render once a minute while viewing today. */
  const [nowMin, setNowMin] = useState(() => displayMinutesOfDay(new Date()));
  useEffect(() => {
    if (!isToday) return;
    const t = window.setInterval(() => {
      setNowMin(displayMinutesOfDay(new Date()));
    }, 60_000);
    return () => window.clearInterval(t);
  }, [isToday]);

  /* Hovering any leg of a visit lights up its other legs. */
  const [hoveredVisit, setHoveredVisit] = useState<string | null>(null);
  /* Where the visit she is placing would land right now. */
  const [ghost, setGhost] = useState<{ colId: string | null; min: number } | null>(null);
  useEffect(() => { if (!placing) setGhost(null); }, [placing]);

  const columns = useMemo<DayColumn[]>(() => {
    const cols: DayColumn[] =
      groupBy === "provider"
        ? providers.map((p) => ({
            id: p.id,
            name: p.name,
            dot: DOT_CLASSES[p.color] ?? "bg-zinc-400",
            tint: TINT_CLASSES[p.color] ?? "",
            color: p.color ?? null,
          }))
        : rooms
            .filter((r) => r.active || appointments.some((a) => visitSegments(a).some((s) => s.room_id === r.id)))
            .map((r) => ({
              id: r.id,
              name: r.active ? r.name : `${r.name} (retired)`,
              dot: DOT_CLASSES[r.color] ?? "bg-zinc-500",
              tint: TINT_CLASSES[r.color] ?? "",
              color: r.color ?? null,
              pickup: r.kind === "pickup",
            }));
    const known = new Set(cols.map((c) => c.id));
    const hasUnassigned = appointments.some((a) =>
      visitSegments(a).some((s) => {
        const key = groupBy === "provider" ? s.provider_id : s.room_id;
        return !key || !known.has(key);
      })
    );
    if (hasUnassigned) cols.push({ id: null, name: "Unassigned", dot: "bg-zinc-600", tint: "", color: null });
    return cols;
  }, [appointments, providers, rooms, groupBy]);

  const byColumn = useMemo(() => {
    const map = new Map<string | null, SegmentPlacement[]>();
    for (const col of columns) map.set(col.id, []);
    for (const a of appointments) {
      const segments = visitSegments(a);
      segments.forEach((segment, index) => {
        const key = groupBy === "provider" ? (segment.provider_id ?? null) : (segment.room_id ?? null);
        const bucket = map.get(key) ?? map.get(null);
        if (bucket) bucket.push({ visit: a, segment, index, total: segments.length });
      });
    }
    for (const list of map.values()) {
      list.sort((x, y) => new Date(x.segment.starts_at).getTime() - new Date(y.segment.starts_at).getTime());
    }
    return map;
  }, [appointments, columns, groupBy]);

  /**
   * Seats that share a room (her lounge): while one is in use, the other
   * can't be booked. Shown as a light band so the desk sees WHY the consult
   * chair is off-limits at 10:00 instead of a mysteriously empty column.
   */
  const privacyByColumn = useMemo(() => {
    const map = new Map<string, { key: string; starts_at: string; ends_at: string; mate: string }[]>();
    if (groupBy !== "room") return map;
    for (const r of rooms) {
      if (!r.active || !r.privacy_group) continue;
      const mates = new Map(rooms.filter((o) => o.id !== r.id && o.privacy_group === r.privacy_group).map((o) => [o.id, o.name]));
      const bands: { key: string; starts_at: string; ends_at: string; mate: string }[] = [];
      for (const a of appointments) {
        if (a.status === "cancelled" || a.status === "no_show") continue;
        for (const sg of visitSegments(a)) {
          if (sg.room_id && mates.has(sg.room_id)) {
            bands.push({ key: `${a.id}:${sg.id}`, starts_at: sg.starts_at, ends_at: sg.ends_at, mate: mates.get(sg.room_id)! });
          }
        }
      }
      map.set(r.id, bands);
    }
    return map;
  }, [appointments, rooms, groupBy]);

  /** Drag snap per column: a seat's own booking step, else the practice's. */
  const gridFor = useCallback((colId: string | null) => {
    if (groupBy === "room" && colId) {
      const seat = rooms.find((r) => r.id === colId);
      if (seat?.slot_minutes) return seat.slot_minutes;
    }
    return bookingGridMinutes();
  }, [rooms, groupBy]);

  const columnName = useCallback(
    (colId: string | null) => columns.find((c) => c.id === colId)?.name ?? "",
    [columns],
  );

  /**
   * Column-scoped blocked time, and the blocks that belong to no column here
   * — she blocked HERSELF while the grid was grouped by seat, which is why
   * "I blocked out this Wednesday … but it's not showing on the calendar".
   */
  const { byColumn: blocksByColumn, across: blocksAcross } = useMemo(
    () => splitBlocks(blocks, columns.map((c) => c.id), groupBy),
    [blocks, columns, groupBy],
  );

  if (columns.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={groupBy === "provider" ? Users : DoorOpen}
          title={groupBy === "provider" ? "No providers set up" : "No rooms set up"}
          description={
            groupBy === "provider"
              ? "Add providers in Settings to start building the day schedule."
              : "Add treatment rooms in Settings to view the schedule by room."
          }
        />
      </div>
    );
  }

  const hours: number[] = [];
  for (let h = DAY_START_MIN / 60; h <= DAY_END_MIN / 60; h++) hours.push(h);
  const quarters: number[] = [];
  for (let m = DAY_START_MIN + 15; m < DAY_END_MIN; m += 15) quarters.push(m);

  const showNowLine = isToday && nowMin >= DAY_START_MIN && nowMin <= DAY_END_MIN;

  return (
    <div style={{ minWidth: GUTTER_PX + columns.length * COL_MIN_PX }}>
      {/* Column headers */}
      <div className="sticky top-0 z-30 flex border-b border-zinc-800/70 bg-zinc-950">
        <div className="sticky left-0 z-10 w-14 shrink-0 bg-zinc-950" />
        {columns.map((col) => (
          <div
            key={col.id ?? "unassigned"}
            className={cn(
              "flex min-w-[10rem] flex-1 basis-0 items-center gap-2 border-l border-zinc-800/40 px-3 py-2.5",
              col.tint,
            )}
          >
            <span className={cn("h-2 w-2 shrink-0 rounded-full", col.dot)} />
            <span className="truncate text-sm font-medium text-zinc-200">{col.name}</span>
            {/* Nobody would guess the column is clickable, so it says so. */}
            {col.pickup && onPickup && !placing && (
              <span className="shrink-0 text-[10px] leading-tight text-zinc-500">click a time</span>
            )}
          </div>
        ))}
      </div>

      {/* Grid canvas */}
      <div className="relative flex">
        {/* Time gutter */}
        <div className="sticky left-0 z-20 w-14 shrink-0 bg-zinc-950" style={{ height: CANVAS_PX }}>
          {hours.map((h) => (
            <span
              key={h}
              className="absolute right-2 -translate-y-1/2 text-[10px] font-semibold text-zinc-400"
              style={{ top: Math.min(Math.max(yFromMinutes(h * 60), 8), CANVAS_PX - 8) }}
            >
              {hourLabel(h)}
            </span>
          ))}
          {hours.slice(0, -1).map((h) => (
            <span
              key={`${h}:30`}
              className="absolute right-2 -translate-y-1/2 text-[9px] text-zinc-500"
              style={{ top: yFromMinutes(h * 60 + 30) }}
            >
              :30
            </span>
          ))}
        </div>

        {/* Provider / room columns */}
        {columns.map((col) => (
          <div
            key={col.id ?? "unassigned"}
            data-schedule-col={col.id ?? ""}
            className={cn(
              "relative min-w-[10rem] flex-1 basis-0 border-l border-zinc-800/40",
              // While she is placing a visit the whole column is the target,
              // so the cards underneath mustn't swallow the click.
              placing && "cursor-copy [&_button]:pointer-events-none",
              col.pickup && !placing && onPickup && "cursor-copy",
            )}
            style={{ height: CANVAS_PX }}
            onMouseMove={placing ? (e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              // Placing by hand snaps to the quarter hour, not the chair's
              // half-hourly booking clock: the half hours are what clients
              // are OFFERED, and "usually the desk is controlling where the
              // ppl get placed".
              const grid = Math.min(gridFor(col.id), 15);
              const raw = DAY_START_MIN + ((e.clientY - rect.top) / HOUR_PX) * 60;
              const min = Math.min(
                Math.max(Math.round(raw / grid) * grid, DAY_START_MIN),
                DAY_END_MIN - grid,
              );
              setGhost({ colId: col.id, min });
            } : undefined}
            onClick={
              placing && onPlace
                ? () => {
                    if (!ghost) return;
                    onPlace(ghost.colId, new Date(day.getTime() + ghost.min * 60_000).toISOString());
                  }
                // Her pick-up column: click the time you want and say who is
                // coming and what for. A click that landed on a card already
                // opened that visit, so only free space reaches here.
                : col.pickup && onPickup
                  ? (e) => {
                      if ((e.target as HTMLElement).closest("button")) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const raw = DAY_START_MIN + ((e.clientY - rect.top) / HOUR_PX) * 60;
                      const min = Math.min(
                        Math.max(Math.round(raw / 15) * 15, DAY_START_MIN),
                        DAY_END_MIN - 15,
                      );
                      onPickup(new Date(day.getTime() + min * 60_000).toISOString());
                    }
                  : undefined
            }
          >
            {/* The visit, following the pointer until she clicks. */}
            {placing && ghost && ghost.colId === col.id && (
              <div
                data-place-ghost
                className="pointer-events-none absolute inset-x-1 z-30 rounded-md border-2 border-dashed border-indigo-400 bg-indigo-500/20 px-2 py-1"
                style={{
                  top: yFromMinutes(ghost.min),
                  height: Math.max((placing.minutes / 60) * HOUR_PX, 22),
                }}
              >
                <p className="truncate text-[11px] font-semibold leading-4 text-indigo-100">
                  {formatTime(new Date(day.getTime() + ghost.min * 60_000).toISOString())}
                </p>
                <p className="truncate text-[10px] leading-4 text-indigo-200/90">{placing.label}</p>
              </div>
            )}
            {/* Every quarter hour, weighted so the eye can count: the hour
                is a real line, the half hour is softer, the quarters are
                just enough to land a 15-minute visit against. */}
            {quarters.map((min) => (
              <div
                key={min}
                data-grid-min={min}
                className={cn(
                  "pointer-events-none absolute inset-x-0 border-t",
                  // Her app is light-themed by the palette swap in globals.css
                  // (zinc-800 renders #dcdce1, zinc-700 #c2c2ca), so the
                  // dark-theme weights read as three shades of almost-white.
                  // These are the numbers that actually look like a ruler on
                  // her screen: "darken the lines to be able to see the time".
                  min % 60 === 0 ? "border-zinc-600"
                    : min % 30 === 0 ? "border-zinc-700"
                    : "border-zinc-800",
                )}
                style={{ top: yFromMinutes(min) }}
              />
            ))}
            {(col.id ? blocksByColumn.get(col.id) ?? [] : []).map((b) => (
              <BlockedBand key={b.id} block={b} day={day} onDelete={onDeleteBlock} />
            ))}
            {col.id && (privacyByColumn.get(col.id) ?? []).map((b) => (
              <PrivacyBand key={b.key} startsAt={b.starts_at} endsAt={b.ends_at} mate={b.mate} day={day} />
            ))}
            {(byColumn.get(col.id) ?? []).map((p) => (
              <SegmentBlock
                key={`${p.visit.id}:${p.segment.id}`}
                placement={p}
                day={day}
                linked={hoveredVisit === p.visit.id}
                onHover={setHoveredVisit}
                onSelect={onSelect}
                onMove={onMove}
                gridFor={gridFor}
                columnName={columnName}
                colColor={col.color}
              />
            ))}
          </div>
        ))}

        {/* Blocked time that isn't any one column's — across the whole day.
            Bands that share an hour step their labels down so neither hides
            the other; a band on its own always sits at the top of its hour. */}
        {blocksAcross.map((b, i) => (
          <AcrossBand
            key={b.id}
            block={b}
            day={day}
            lane={blocksAcross.slice(0, i).filter((o) =>
              new Date(o.starts_at) < new Date(b.ends_at) &&
              new Date(o.ends_at) > new Date(b.starts_at)).length}
            onDelete={onDeleteBlock}
          />
        ))}

        {/* Now line */}
        {showNowLine && (
          <div
            className="pointer-events-none absolute right-0 z-20"
            style={{ left: GUTTER_PX, top: yFromMinutes(nowMin) }}
          >
            <div className="relative h-px bg-rose-500">
              <span className="absolute -left-1 -top-[3px] h-[7px] w-[7px] rounded-full bg-rose-500" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Blocked time with no column of its own — a provider taken off the board
 * while the grid is grouped by seat, and the other way round. It isn't one
 * chair's hour, so it bands the whole day rather than picking a column at
 * random, which is what it took to make "I blocked out Wednesday 1-2 for a
 * meeting" actually appear.
 */
function AcrossBand({
  block,
  day,
  lane,
  onDelete,
}: {
  block: ScheduleBlock;
  day: Date;
  /** How many bands already drawn share this hour — steps the label down. */
  lane: number;
  onDelete: (b: ScheduleBlock) => void;
}) {
  const startMin = minutesInDay(block.starts_at, day);
  const endMin = Math.max(minutesInDay(block.ends_at, day), startMin + 15);
  const top = Math.min(Math.max(yFromMinutes(startMin), 0), CANVAS_PX - 16);
  const bottom = Math.min(Math.max(yFromMinutes(endMin), top + 16), CANVAS_PX);
  const height = bottom - top;
  const who = block.provider_name ?? block.room_name ?? "Blocked";
  const when = `${formatTime(block.starts_at)} – ${formatTime(block.ends_at)}`;

  return (
    <div
      // No `overflow-hidden`: it would make this band the sticky label's
      // scrollport, and the label would slide off the left of a grid wide
      // enough to scroll — which is every day she has ten seats open.
      className="pointer-events-none absolute right-0 z-[6] border-y border-zinc-500/40 bg-zinc-500/15"
      style={{
        left: GUTTER_PX,
        top,
        height,
        backgroundImage:
          "repeating-linear-gradient(135deg, rgba(0,0,0,0.07) 0px, rgba(0,0,0,0.07) 3px, transparent 3px, transparent 9px)",
      }}
      title={`${who} blocked ${when}${block.reason ? ` · ${block.reason}` : ""}`}
    >
      {/* Rides the horizontal scroll so the label is readable at any column.
          It parks clear of the sticky time gutter (GUTTER_PX), which paints
          over the grid and would otherwise eat the first half of the name. */}
      <div
        className="pointer-events-auto sticky left-16 inline-flex max-w-[26rem] items-center gap-1.5 rounded-md border border-zinc-600/50 bg-zinc-900/90 px-1.5 py-0.5"
        style={{ marginTop: Math.min(lane * 18, Math.max(height - 20, 0)) }}
      >
        <span className="truncate text-[10px] font-semibold leading-4 text-zinc-200">
          {who} blocked
        </span>
        <span className="shrink-0 text-[10px] leading-4 text-zinc-400">{when}</span>
        {block.reason && (
          <span className="truncate text-[10px] leading-4 text-zinc-500">· {block.reason}</span>
        )}
        <button
          type="button"
          onClick={() => onDelete(block)}
          aria-label="Remove blocked time"
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-zinc-500 hover:bg-zinc-700/60 hover:text-rose-300"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

/**
 * One leg of a visit. Multi-segment visits get a ring and an "n/total" marker
 * so the two halves of a Consult → Drip visit read as one booking, and any of
 * them opens the same detail modal.
 */
function SegmentBlock({
  placement,
  day,
  linked,
  onHover,
  onSelect,
  onMove,
  gridFor,
  columnName,
  colColor,
}: {
  placement: SegmentPlacement;
  day: Date;
  linked: boolean;
  onHover: (id: string | null) => void;
  onSelect: (a: Appointment) => void;
  onMove: (
    placement: SegmentPlacement,
    change: { minutesDelta: number; targetColId: string | null },
  ) => Promise<boolean>;
  /** Minutes to snap to in a column: 30 for chairs, 15 for phone/video/pick-up. */
  gridFor: (colId: string | null) => number;
  /** What to call the column she's dragging over. */
  columnName: (colId: string | null) => string;
  /** The colour of the column this segment sits in — the card matches it. */
  colColor: string | null;
}) {
  /* Drag to move — her ask, verbatim: "if a client calls running late, just
     click, hold, and drag the card down an hour", and sideways to another
     chair. Vertically the dragged card's START snaps to the booking grid
     (30 minutes: 9:00, 9:30 …), so an old 9:15 visit lands on the grid too;
     horizontal hit-tests the column under the pointer. A small threshold
     keeps an ordinary click a click. */
  const [drag, setDrag] = useState<{ dy: number; dx: number; overCol: string | null; minutesDelta: number } | null>(null);
  const [savingMove, setSavingMove] = useState(false);
  const dragState = useRef<{ startX: number; startY: number; moved: boolean; homeLeft: number }>(
    { startX: 0, startY: 0, moved: false, homeLeft: 0 },
  );

  const beginDrag = (e: React.PointerEvent) => {
    if (savingMove) return;
    if (a.status === "completed" || a.status === "cancelled" || a.status === "no_show") return;
    const home = (e.currentTarget as HTMLElement).closest<HTMLElement>("[data-schedule-col]");
    dragState.current = {
      startX: e.clientX, startY: e.clientY, moved: false,
      homeLeft: home?.getBoundingClientRect().left ?? 0,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const trackDrag = (e: React.PointerEvent) => {
    const st = dragState.current;
    if (!(e.currentTarget as HTMLElement).hasPointerCapture?.(e.pointerId)) return;
    const dxRaw = e.clientX - st.startX;
    const dyRaw = e.clientY - st.startY;
    if (!st.moved && Math.abs(dxRaw) < 6 && Math.abs(dyRaw) < 6) return;
    st.moved = true;
    // Which column is the pointer over?
    let overCol: string | null = null;
    let overLeft: number | null = null;
    for (const el of document.querySelectorAll<HTMLElement>("[data-schedule-col]")) {
      const r = el.getBoundingClientRect();
      if (e.clientX >= r.left && e.clientX < r.right) {
        overCol = el.dataset.scheduleCol || null;
        overLeft = r.left;
        break;
      }
    }
    // Ten columns don't fit on her screen: dragging to the edge walks the
    // day sideways, so a visit can reach a chair that's off-screen.
    const scroller = (e.currentTarget as HTMLElement).closest<HTMLElement>(".overflow-auto");
    if (scroller) {
      const r = scroller.getBoundingClientRect();
      if (e.clientX > r.right - 56) scroller.scrollLeft += 18;
      else if (e.clientX < r.left + 56 + GUTTER_PX) scroller.scrollLeft -= 18;
    }
    // Snap to the grid of the seat it's landing in: a phone consult can go
    // to 9:15, a drip chair stays on the half hour.
    const grid = gridFor(overCol ?? (seg.room_id ?? seg.provider_id ?? null));
    const fromMin = minutesInDay(seg.starts_at, day);
    const toMin = Math.round((fromMin + (dyRaw / HOUR_PX) * 60) / grid) * grid;
    const dy = ((toMin - fromMin) / 60) * HOUR_PX;
    // The card follows the pointer into the other chair's column. Without
    // this it stayed put while only the time moved, so a sideways drag
    // looked like nothing was happening — it was landing all along.
    const dx = overLeft !== null ? overLeft - st.homeLeft : dxRaw;
    setDrag({ dy, dx, overCol, minutesDelta: toMin - fromMin });
  };
  const endDrag = async (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    const st = dragState.current;
    const d = drag;
    setDrag(null);
    if (!st.moved || !d) return;
    const minutesDelta = Math.round((d.dy / HOUR_PX) * 60);
    const homeCol = (seg.room_id ?? seg.provider_id) ?? null;
    const targetColId = d.overCol && d.overCol !== "" && d.overCol !== homeCol ? d.overCol : null;
    if (minutesDelta === 0 && !targetColId) return;
    setSavingMove(true);
    await onMove(placement, { minutesDelta, targetColId });
    setSavingMove(false);
  };
  const { visit: a, segment: seg, index, total } = placement;

  const startMin = minutesInDay(seg.starts_at, day);
  const endMin = Math.max(minutesInDay(seg.ends_at, day), startMin + 15);

  const rawTop = yFromMinutes(startMin);
  const rawBottom = yFromMinutes(endMin);
  const top = Math.min(Math.max(rawTop, 0), CANVAS_PX - 20);
  const bottom = Math.min(Math.max(rawBottom, top + 20), CANVAS_PX);
  const height = bottom - top;

  // How much text actually fits: each line is 16px and the padding costs 8px,
  // so a 15-minute visit (16px tall) gets one line, a 45-minute one gets two.
  // Without this the last line was sliced through the middle.
  /**
   * How much fits, and what wins when it doesn't.
   *
   * The dose is the point: her services are named "SPEC-VC 5,000 mg", and she
   * reads the room off the grid without opening anything ("we need the dose on
   * the card"). So the treatment ALWAYS shows. The clock is what gets dropped
   * first, because the block's position on a time grid already says when it is.
   *   full   — comfortable: time, client, treatment
   *   dense  — same three, tightened, for a 45-minute visit
   *   single — a quarter-hour visit: client and treatment on one line
   */
  const density = height >= 56 ? "full" : height >= 34 ? "dense" : "single";

  // "the box is the same shade as the chair just darker". The seat wins; the
  // treatment's own colour is the fallback for a column with none (Unassigned).
  const colorClass = visitColorClass(colColor, seg.service_color ?? a.service_color);
  const inactive = a.status === "cancelled" || a.status === "no_show";
  const status = STATUS_META[a.status];
  const forms = formsMeta(a);
  const multi = total > 1;

  /* Cleanup buffer — faint hatched extension below this segment. */
  let bufferTop: number | null = null;
  let bufferHeight = 0;
  if (seg.blocked_until) {
    const blockedMin = minutesInDay(seg.blocked_until, day);
    if (blockedMin > endMin && rawBottom < CANVAS_PX) {
      bufferTop = bottom;
      bufferHeight = Math.max(Math.min(yFromMinutes(blockedMin), CANVAS_PX) - bottom, 0);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => { if (!dragState.current.moved) onSelect(a); }}
        onPointerDown={beginDrag}
        onPointerMove={trackDrag}
        onPointerUp={endDrag}
        onMouseEnter={() => onHover(a.id)}
        onMouseLeave={() => onHover(null)}
        onFocus={() => onHover(a.id)}
        onBlur={() => onHover(null)}
        title={multi ? `${visitTitle(a)} · part ${index + 1} of ${total}` : visitTitle(a)}
        className={cn(
          "absolute inset-x-1 z-10 overflow-hidden rounded-md border-l-[3px] px-2 text-left transition-[filter] hover:brightness-95",
          density === "single" ? "py-0" : density === "dense" ? "py-0.5" : "py-1",
          colorClass,
          inactive && "opacity-50",
          multi && "ring-1 ring-inset ring-indigo-400/40",
          linked && multi && "ring-2 ring-indigo-400/70 brightness-95"
        )}
        style={{
          top: top + (drag?.dy ?? 0),
          height,
          ...(drag ? {
            transform: `translateX(${drag.dx}px)`,
            zIndex: 40, opacity: 0.9, cursor: "grabbing", touchAction: "none",
            boxShadow: "0 8px 24px rgba(0,0,0,.35)",
          } : {}),
          ...(savingMove ? { opacity: 0.5 } : {}),
        }}
      >
        {/* Where it will land, while she's still holding it. */}
        {drag && (
          <span
            data-drag-time
            className="absolute left-1 top-0.5 z-10 max-w-[70%] truncate rounded bg-zinc-900/95 px-1.5 py-px text-[10px] font-semibold text-zinc-100 shadow"
          >
            {formatTime(new Date(new Date(seg.starts_at).getTime() + drag.minutesDelta * 60_000).toISOString())}
            {drag.overCol && drag.overCol !== (seg.room_id ?? seg.provider_id)
              ? ` · ${columnName(drag.overCol)}`
              : ""}
          </span>
        )}
        <span className="absolute right-1 top-1 flex flex-col items-end gap-0.5">
          {/* "If I add the drip to that appointment it separates the
              appointment on the screen is it possible to keep them together"
              - it never was two visits, but at rest the two blocks looked
              like two, because the only thing tying them together was a ring
              that appeared on hover. Saying "1/2" on each one makes it one
              visit at a glance, without hovering anything. */}
          {multi && (
            <span className="rounded bg-zinc-900/80 px-1 py-px text-[9px] font-semibold text-zinc-100">
              {index + 1}/{total}
            </span>
          )}
          <span className={cn("rounded px-1 py-px text-[9px] font-semibold", status.chip)}>
            {status.label}
          </span>
          {forms && density === "full" && (
            <span className={cn("rounded px-1 py-px text-[9px] font-semibold", forms.chip)}>
              {forms.label}
            </span>
          )}
        </span>
        {/* A quarter-hour visit can't hold three lines — collapse to one, and
            keep the CLIENT rather than the clock, since the row's position
            already says when it is. */}
        {density === "single" ? (
          <p className="flex items-baseline gap-1.5 truncate pr-16 text-[11px] leading-4">
            <span className={cn("shrink-0 max-w-[55%] truncate font-semibold", inactive && "line-through")}>
              {a.contact_name || "Walk-in"}{a.tag_emojis ? ` ${a.tag_emojis}` : ""}
            </span>
            {seg.service_name && (
              <span className="truncate opacity-80">{seg.service_name}</span>
            )}
          </p>
        ) : (
        <>
        <p className={cn(
          "flex items-center gap-1 pr-16 font-medium opacity-80",
          density === "dense" ? "text-[9px] leading-[13px]" : "text-[10px] leading-4",
        )}>
          <span className="truncate">
            {formatTime(seg.starts_at)} – {formatTime(seg.ends_at)}
          </span>
          {multi && (
            <span className="shrink-0 rounded bg-black/10 px-1 text-[9px] font-semibold tabular-nums">
              {index + 1}/{total}
            </span>
          )}
        </p>
        <p className={cn(
          "truncate pr-16 font-semibold",
          density === "dense" ? "text-[11px] leading-[14px]" : "text-xs leading-4",
          inactive && "line-through",
        )}>
          {a.contact_name || "Walk-in"}{a.tag_emojis ? ` ${a.tag_emojis}` : ""}
        </p>
        {seg.service_name && (
          <p className={cn(
            "truncate pr-16 opacity-80",
            density === "dense" ? "text-[10px] leading-[13px]" : "text-[11px] leading-4",
          )}>
            {seg.service_name}
          </p>
        )}
        </>
        )}
      </button>
      {bufferTop !== null && bufferHeight > 2 && (
        <div
          className={cn("pointer-events-none absolute inset-x-1 rounded-b-md border-l-[3px] opacity-40", colorClass)}
          style={{
            // The cleanup band belongs to the card, so it travels with it.
            top: bufferTop + (drag?.dy ?? 0),
            ...(drag ? { transform: `translateX(${drag.dx}px)`, zIndex: 39 } : {}),
            height: bufferHeight,
            backgroundImage:
              "repeating-linear-gradient(135deg, rgba(0,0,0,0.10) 0px, rgba(0,0,0,0.10) 3px, transparent 3px, transparent 8px)",
          }}
        />
      )}
    </>
  );
}

/**
 * The seat's room-mate is in use (her lounge: a drip in IV Lounge 1 means no
 * private consult in Consult Lounge 2). Faint, and never in the way of a
 * click or a drop — the server refuses a clashing booking with the reason.
 */
function PrivacyBand({ startsAt, endsAt, mate, day }: { startsAt: string; endsAt: string; mate: string; day: Date }) {
  const startMin = minutesInDay(startsAt, day);
  const endMin = Math.max(minutesInDay(endsAt, day), startMin + 15);
  const top = Math.min(Math.max(yFromMinutes(startMin), 0), CANVAS_PX - 16);
  const bottom = Math.min(Math.max(yFromMinutes(endMin), top + 16), CANVAS_PX);
  return (
    <div
      className="pointer-events-none absolute inset-x-1 z-[4] overflow-hidden rounded-md border border-dashed border-zinc-600/50 bg-zinc-500/5 px-2 py-1"
      style={{ top, height: bottom - top }}
      title={`${mate} is in use then, so this seat won't be private`}
      data-privacy-band
    >
      <p className="truncate text-[10px] leading-4 text-zinc-500">In use next door ({mate})</p>
    </div>
  );
}

/** Provider or room taken off the board — flat striped band, X to release. */
function BlockedBand({
  block,
  day,
  onDelete,
}: {
  block: ScheduleBlock;
  day: Date;
  onDelete: (b: ScheduleBlock) => void;
}) {
  const startMin = minutesInDay(block.starts_at, day);
  const endMin = Math.max(minutesInDay(block.ends_at, day), startMin + 15);

  const top = Math.min(Math.max(yFromMinutes(startMin), 0), CANVAS_PX - 16);
  const bottom = Math.min(Math.max(yFromMinutes(endMin), top + 16), CANVAS_PX);
  const height = bottom - top;

  return (
    <div
      className="absolute inset-x-1 z-[5] overflow-hidden rounded-md border border-zinc-700/50 bg-zinc-800/40 px-2 py-1"
      style={{
        top,
        height,
        backgroundImage:
          "repeating-linear-gradient(135deg, rgba(0,0,0,0.08) 0px, rgba(0,0,0,0.08) 3px, transparent 3px, transparent 8px)",
      }}
      title={block.reason ? `Blocked — ${block.reason}` : "Blocked"}
    >
      <button
        type="button"
        onClick={() => onDelete(block)}
        aria-label="Remove blocked time"
        className="absolute right-0.5 top-0.5 inline-flex h-5 w-5 items-center justify-center rounded text-zinc-500 hover:bg-zinc-700/60 hover:text-rose-300"
      >
        <X className="h-3 w-3" />
      </button>
      <p className="pr-5 text-[10px] font-semibold uppercase tracking-wide leading-4 text-zinc-400">Blocked</p>
      {block.reason && <p className="truncate pr-5 text-[10px] leading-4 text-zinc-500">{block.reason}</p>}
    </div>
  );
}

/* ─── Week view ────────────────────────────────────────────────────────────── */

function WeekView({
  appointments,
  blocks,
  weekStart,
  todayKey,
  onSelect,
  onOpenDay,
}: {
  appointments: Appointment[];
  blocks: ScheduleBlock[];
  weekStart: Date;
  todayKey: string;
  onSelect: (a: Appointment) => void;
  onOpenDay: (d: Date) => void;
}) {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const byDay = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const a of appointments) {
      const key = displayDayKey(new Date(a.starts_at));
      const list = map.get(key) ?? [];
      list.push(a);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((x, y) => new Date(x.starts_at).getTime() - new Date(y.starts_at).getTime());
    }
    return map;
  }, [appointments]);

  /**
   * Blocked time, so a week that's been held for a meeting doesn't read as
   * wide open. A block that runs past midnight shows on every day it covers.
   */
  const blocksByDay = useMemo(() => {
    const map = new Map<string, ScheduleBlock[]>();
    for (const day of days) {
      const from = displayDayStart(displayDayKey(day)).getTime();
      const to = displayDayStart(displayDayKey(addDays(day, 1))).getTime();
      const hits = blocks.filter((b) => {
        const s = new Date(b.starts_at).getTime();
        const e = new Date(b.ends_at).getTime();
        return s < to && e > from;
      });
      if (hits.length > 0) {
        hits.sort((x, y) => new Date(x.starts_at).getTime() - new Date(y.starts_at).getTime());
        map.set(displayDayKey(day), hits);
      }
    }
    return map;
  }, [blocks, days]);

  return (
    <div className="grid min-w-[56rem] grid-cols-7 gap-px bg-zinc-800/40 p-px">
      {days.map((day) => {
        const key = displayDayKey(day);
        const dayAppts = byDay.get(key) ?? [];
        const overflow = dayAppts.length - 6;
        const isToday = key === todayKey;
        return (
          <div key={key} className="flex min-h-[16rem] flex-col bg-zinc-950">
            <button
              type="button"
              onClick={() => onOpenDay(day)}
              className={cn(
                "sticky top-0 z-10 flex items-baseline gap-1.5 border-b border-zinc-800/70 bg-zinc-950 px-2.5 py-2 text-left hover:bg-zinc-900/60",
                isToday && "border-b-indigo-500/50"
              )}
            >
              <span className={cn("text-xs font-semibold", isToday ? "text-indigo-300" : "text-zinc-300")}>
                {formatDate(day, { weekday: "short" })}
              </span>
              <span className={cn("text-xs", isToday ? "text-indigo-400" : "text-zinc-500")}>
                {formatDate(day, { month: "short", day: "numeric" })}
              </span>
            </button>
            <div className="flex flex-col gap-1 p-1.5">
              {(blocksByDay.get(key) ?? []).map((b) => (
                <div
                  key={b.id}
                  title={b.reason ? `Blocked — ${b.reason}` : "Blocked"}
                  className="rounded-md border border-zinc-600/40 bg-zinc-500/15 px-1.5 py-1"
                >
                  <p className="truncate text-[10px] font-semibold leading-4 text-zinc-300">
                    {b.provider_name ?? b.room_name ?? "Blocked"} blocked
                  </p>
                  <p className="truncate text-[10px] leading-4 text-zinc-500">
                    {formatTime(b.starts_at)} – {formatTime(b.ends_at)}
                    {b.reason ? ` · ${b.reason}` : ""}
                  </p>
                </div>
              ))}
              {dayAppts.slice(0, 6).map((a) => {
                const inactive = a.status === "cancelled" || a.status === "no_show";
                const forms = formsMeta(a);
                const title = visitTitle(a);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => onSelect(a)}
                    className={cn(
                      "rounded-md border-l-[3px] px-1.5 py-1 text-left transition-[filter] hover:brightness-125",
                      // The seat's colour, exactly as the day grid paints it.
                      visitColorClass(visitSegments(a)[0]?.room_color, a.service_color),
                      inactive && "opacity-50"
                    )}
                  >
                    <p className="truncate text-[10px] font-medium opacity-80">{formatTime(a.starts_at)}</p>
                    <p className={cn("truncate text-[11px] font-semibold", inactive && "line-through")}>
                      {a.contact_name || "Walk-in"}
                    </p>
                    <p className="truncate text-[10px] opacity-80">{title}</p>
                    {forms && (
                      <span className={cn("mt-0.5 inline-block rounded px-1 py-px text-[9px] font-semibold", forms.chip)}>
                        {forms.label}
                      </span>
                    )}
                  </button>
                );
              })}
              {overflow > 0 && (
                <button
                  type="button"
                  onClick={() => onOpenDay(day)}
                  className="rounded-md px-1.5 py-1 text-left text-[11px] font-medium text-zinc-400 hover:bg-zinc-900/60 hover:text-zinc-200"
                >
                  +{overflow} more
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Detail dialog ────────────────────────────────────────────────────────── */

interface FormRequestSummary {
  id: string;
  template_name: string;
  status: "sent" | "viewed" | "completed";
  url: string;
}

const FORM_STATUS_META: Record<FormRequestSummary["status"], { label: string; className: string }> = {
  sent: { label: "Sent", className: "border-zinc-700 bg-zinc-800/60 text-zinc-300" },
  viewed: { label: "Opened", className: "border-amber-500/30 bg-amber-500/10 text-amber-300" },
  completed: { label: "Completed ✓", className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" },
};

function AppointmentDetailModal({
  appointment: a,
  onClose,
  onUpdated,
  onReschedule,
}: {
  appointment: Appointment;
  onClose: () => void;
  onUpdated: (a: Appointment) => void;
  onReschedule: (a: Appointment) => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [forms, setForms] = useState<FormRequestSummary[]>([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ requests: FormRequestSummary[] }>("/forms/requests", { appointment_id: a.id });
        if (!cancelled) setForms(res.requests);
      } catch {
        if (!cancelled) setForms([]);
      } finally {
        if (!cancelled) setFormsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [a.id]);

  const patchStatus = async (status: Appointment["status"], successMsg: string, closeAfter = false) => {
    setBusy(status);
    try {
      const res = await api.patch<{ appointment: Appointment }>(`/appointments/${a.id}`, { status });
      onUpdated(res.appointment);
      toast.success(successMsg);
      if (closeAfter) onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update appointment");
    } finally {
      setBusy(null);
    }
  };

  const clientName = a.contact_name || "Walk-in";
  const segments = visitSegments(a);
  const multi = segments.length > 1;
  const title = visitTitle(a);
  const totalCents = visitTotalCents(a);
  const formsProgress = formsMeta(a);

  const handleCancel = async () => {
    const ok = await confirm({
      title: "Cancel this appointment?",
      description: `${title} for ${clientName} on ${formatDate(a.starts_at)} at ${formatTime(a.starts_at)}.`,
      confirmLabel: "Cancel appointment",
      danger: true,
    });
    if (ok) await patchStatus("cancelled", "Appointment cancelled", true);
  };

  const handleNoShow = async () => {
    const ok = await confirm({
      title: "Mark as no-show?",
      description: `${clientName} will be recorded as a no-show for this visit.`,
      confirmLabel: "Mark no-show",
      danger: true,
    });
    if (ok) await patchStatus("no_show", "Marked as no-show", true);
  };

  const status = STATUS_META[a.status];
  const isActive = ACTIVE_STATUSES.includes(a.status);
  const providerDot = DOT_CLASSES[a.provider_color ?? ""] ?? "bg-zinc-500";

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-center gap-2.5">
          <span>{title}</span>
          <Badge color={status.badge} size="xs">{status.label}</Badge>
        </span>
      }
    >
      <div className="space-y-3 text-sm">
        <DetailRow icon={User} label="Client">
          {a.contact_id ? (
            <Link
              to={`/contacts/${a.contact_id}`}
              state={{ from: "/schedule" }}
              className="font-medium text-indigo-300 hover:text-indigo-200 hover:underline"
            >
              {clientName}
              {a.tag_emojis ? <span className="ml-1" title="Client tags">{a.tag_emojis}</span> : null}
            </Link>
          ) : (
            <span className="text-zinc-200">Walk-in</span>
          )}
        </DetailRow>

        <DetailRow icon={Clock} label="When">
          <span className="text-zinc-200">
            {formatDate(a.starts_at, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            {" · "}
            {formatTime(a.starts_at)} – {formatTime(a.ends_at)}
          </span>
        </DetailRow>

        {!multi && (
          <>
            <DetailRow icon={Users} label="Provider">
              {a.provider_name ? (
                <span className="inline-flex items-center gap-1.5 text-zinc-200">
                  <span className={cn("h-2 w-2 rounded-full", providerDot)} />
                  {a.provider_name}
                </span>
              ) : (
                <span className="text-zinc-500">Unassigned</span>
              )}
            </DetailRow>

            <DetailRow icon={DoorOpen} label="Seat">
              {a.room_name ? (
                <span className="inline-flex items-center gap-1.5 text-zinc-200">
                  <span className={cn("h-2 w-2 shrink-0 rounded-full", DOT_CLASSES[a.room_color ?? "zinc"] ?? "bg-zinc-400")} />
                  {a.room_name}
                </span>
              ) : (
                <span className="text-zinc-500">—</span>
              )}
            </DetailRow>

            {(a.followup_lab_date || a.followup_consult_date) && (
              <DetailRow icon={Clock} label="Follow-ups">
                <span className="text-zinc-200">
                  {[
                    a.followup_lab_date && `Labs due ${formatPlainDate(a.followup_lab_date, { month: "short", day: "numeric", year: "numeric" })}`,
                    a.followup_consult_date && `Consult due ${formatPlainDate(a.followup_consult_date, { month: "short", day: "numeric", year: "numeric" })}`,
                  ].filter(Boolean).join(" · ")}
                </span>
              </DetailRow>
            )}
          </>
        )}

        <DetailRow icon={CalendarDays} label="Total">
          {totalCents != null ? (
            <span className="text-zinc-200">{formatMoney(totalCents)}</span>
          ) : (
            <span className="text-zinc-500">—</span>
          )}
          <span className="ml-2 text-xs text-zinc-500">
            {a.source === "online" ? "Booked online" : "Booked by staff"}
          </span>
        </DetailRow>

        {a.description && (
          <div className="rounded-lg border border-zinc-800/70 bg-zinc-900/30 px-3 py-2 text-xs text-zinc-300 whitespace-pre-wrap">
            {a.description}
          </div>
        )}

        {/* Visit timeline — every service in the order it's performed. */}
        <div className="pt-1">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Visit {multi && <span className="text-zinc-600">· {segments.length} services</span>}
          </p>
          <ol className="space-y-1.5">
            {segments.map((s, i) => (
              <li
                key={s.id}
                className="flex items-start gap-2.5 rounded-lg border border-zinc-800/70 bg-zinc-900/30 px-3 py-2"
              >
                {multi && (
                  <span className="mt-px inline-flex h-4 w-4 shrink-0 items-center justify-center rounded bg-zinc-800 text-[10px] font-semibold tabular-nums text-zinc-400">
                    {i + 1}
                  </span>
                )}
                <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", DOT_CLASSES[s.service_color ?? ""] ?? "bg-zinc-500")} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-zinc-100">
                    {formatTime(s.starts_at)} – {formatTime(s.ends_at)} · {s.service_name ?? "Service"}
                  </p>
                  <p className="truncate text-[11px] text-zinc-500">
                    {s.provider_name ?? "Unassigned"}
                    {s.room_name ? ` · ${s.room_name}` : ""}
                  </p>
                  {(s.addons ?? []).length > 0 && (
                    <p className="mt-0.5 text-[11px] text-emerald-300/90" data-segment-addons>
                      + {s.addons.map((x) => `${x.name.replace(/^Add /, "")} (${formatMoney(x.price_cents)})`).join(", ")}
                    </p>
                  )}
                </div>
                {s.price_cents != null && (
                  <span className="shrink-0 text-xs tabular-nums text-zinc-400">{formatMoney(s.price_cents)}</span>
                )}
              </li>
            ))}
          </ol>
        </div>

        {/* Forms */}
        <div className="pt-1">
          <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Forms
            {formsProgress && (
              <span className={cn("rounded border px-1.5 py-px text-[10px] font-semibold normal-case", formsProgress.pill)}>
                {formsProgress.done ? "All complete ✓" : `${formsProgress.completed} of ${formsProgress.total} complete`}
              </span>
            )}
          </p>
          {formsLoading ? (
            <div className="flex gap-2">
              <Skeleton className="h-7 w-32" />
              <Skeleton className="h-7 w-28" />
            </div>
          ) : forms.length === 0 ? (
            <p className="text-xs text-zinc-500">No forms for this visit.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {forms.map((f) => {
                const meta = FORM_STATUS_META[f.status];
                return (
                  <a
                    key={f.id}
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs hover:brightness-125",
                      meta.className
                    )}
                  >
                    <span className="font-medium">{f.template_name}</span>
                    <span className="opacity-80">· {meta.label}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ModalFooter>
        {isActive && (
          <div className="mr-auto flex items-center gap-1.5">
            <Button variant="ghost" size="sm" className="text-rose-300 hover:text-rose-200" loading={busy === "cancelled"} onClick={() => void handleCancel()}>
              Cancel
            </Button>
            <Button variant="ghost" size="sm" className="text-amber-300 hover:text-amber-200" loading={busy === "no_show"} onClick={() => void handleNoShow()}>
              No-show
            </Button>
            <Button variant="outline" size="sm" onClick={() => onReschedule(a)}>
              Change visit
            </Button>
          </div>
        )}
        {a.status === "scheduled" && (
          <Button variant="secondary" size="sm" loading={busy === "confirmed"} onClick={() => void patchStatus("confirmed", "Appointment confirmed")}>
            Confirm
          </Button>
        )}
        {(a.status === "scheduled" || a.status === "confirmed") && (
          <Button variant="secondary" size="sm" loading={busy === "arrived"} onClick={() => void patchStatus("arrived", `${clientName} marked as arrived`)}>
            Arrived
          </Button>
        )}
        {(a.status === "arrived" || a.status === "confirmed") && (
          <Button size="sm" onClick={() => navigate(`/checkout?appointment_id=${a.id}`)}>
            Check out
          </Button>
        )}
        {!isActive && (
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex w-24 shrink-0 items-center gap-1.5 text-xs text-zinc-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}



/* ─── Medication pick-up ───────────────────────────────────────────────────── */

/**
 * "Medication pick up this is to pick up meds for example if a pt calls and
 *  says they want to come in and pick up a beauty pkg we would schedule this
 *  in pickup but beauty is a package not a service? Maybe the medication
 *  pickup needs a way to block 15 min pt name and what they are picking up is
 *  it possible to click in the medication pick up from the calendar view and
 *  once we click it it can say create pickup then be able to select name and
 *  select what is to be picked up. Just as a in house"
 *
 * Deliberately not the booking form: what they are collecting is usually a
 * PACKAGE, and the booking form only offers treatments. So this asks for the
 * client, then lists that client's own outstanding vouchers to tick, with a
 * line for anything that is not a voucher. Fifteen minutes, nothing sent.
 */
function PickupModal({
  startIso,
  onClose,
  onSaved,
}: {
  startIso: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Contact[]>([]);
  const [client, setClient] = useState<Contact | null>(null);
  const [vouchers, setVouchers] = useState<PackageBalance[] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [other, setOther] = useState("");
  const [minutes, setMinutes] = useState("15");
  const [saving, setSaving] = useState(false);

  /* Who is coming in. */
  useEffect(() => {
    if (client || query.trim().length < 2) { setResults([]); return; }
    let dead = false;
    const t = window.setTimeout(() => {
      api.get<{ contacts: Contact[] }>("/contacts", { q: query.trim(), limit: 8 })
        .then((d) => { if (!dead) setResults(d.contacts); })
        .catch(() => { if (!dead) setResults([]); });
    }, 200);
    return () => { dead = true; window.clearTimeout(t); };
  }, [query, client]);

  /* What they already have waiting for them. */
  useEffect(() => {
    if (!client) { setVouchers(null); setPicked([]); return; }
    let dead = false;
    api.get<{ balances: PackageBalance[] }>("/pos/package-balances", { contact_id: client.id })
      .then((d) => {
        if (dead) return;
        setVouchers(d.balances.filter((b) => b.sessions_remaining > 0 && !b.practice));
      })
      .catch(() => { if (!dead) setVouchers([]); });
    return () => { dead = true; };
  }, [client]);

  const save = async () => {
    if (!client) return;
    const mins = parseInt(minutes, 10);
    setSaving(true);
    try {
      const r = await api.post<{ collecting: string }>("/frontdesk/pickup", {
        contact_id: client.id,
        starts_at: startIso,
        minutes: Number.isFinite(mins) && mins > 0 ? mins : 15,
        balance_ids: picked,
        other: other.trim() || null,
      });
      toast.success(`Pick-up booked - ${r.collecting}`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't book that pick-up");
      setSaving(false);
    }
  };

  const nothingPicked = picked.length === 0 && !other.trim();

  return (
    <Modal open onClose={onClose} title="Create pick-up" size="md">
      <div className="space-y-4">
        <p className="text-xs text-zinc-400">
          {formatDate(startIso)} at {formatTime(startIso)} in the pick-up column. Nothing is
          sent to the client - this is an in-house note of who is coming.
        </p>

        {/* Who */}
        <div>
          <Label htmlFor="pickup-who" required>Who is collecting</Label>
          {client ? (
            <div className="mt-1 flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2">
              <span className="truncate text-sm text-zinc-100">{fullName(client)}</span>
              <button
                type="button"
                onClick={() => { setClient(null); setQuery(""); }}
                className="text-xs text-zinc-400 hover:text-zinc-200"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <Input
                id="pickup-who"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Start typing a name"
                autoFocus
              />
              {results.length > 0 && (
                <div className="mt-1 overflow-hidden rounded-lg border border-zinc-800">
                  {results.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setClient(c)}
                      className="block w-full px-3 py-1.5 text-left text-sm text-zinc-200 hover:bg-zinc-900/60"
                    >
                      {fullName(c)}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* What */}
        {client && (
          <div>
            <Label>What are they picking up</Label>
            {vouchers === null ? (
              <Skeleton className="mt-1 h-16" />
            ) : vouchers.length === 0 ? (
              <p className="mt-1 text-xs text-zinc-500">
                Nothing outstanding on their file - write it in below.
              </p>
            ) : (
              <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-zinc-800">
                {vouchers.map((b) => (
                  <label
                    key={b.id}
                    className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-zinc-900/60"
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-900 text-fuchsia-500"
                      checked={picked.includes(b.id)}
                      onChange={() => setPicked((prev) =>
                        prev.includes(b.id) ? prev.filter((x) => x !== b.id) : [...prev, b.id])}
                    />
                    <span className="min-w-0 flex-1 truncate text-zinc-200">
                      {b.item_label?.trim() || b.product_name || b.service_name || b.package_name}
                    </span>
                    <span className="shrink-0 text-xs text-zinc-500">{b.sessions_remaining} left</span>
                  </label>
                ))}
              </div>
            )}
            <div className="mt-2">
              <Label htmlFor="pickup-other">Anything else</Label>
              <Input
                id="pickup-other"
                value={other}
                onChange={(e) => setOther(e.target.value)}
                placeholder="e.g. her lab kit"
              />
            </div>
          </div>
        )}

        {client && (
          <div className="w-28">
            <Label htmlFor="pickup-mins">Minutes</Label>
            <Input
              id="pickup-mins"
              type="number"
              min={5}
              max={120}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={saving} disabled={!client || nothingPicked} onClick={() => void save()}>
          Create pick-up
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/* ─── Block time dialog ────────────────────────────────────────────────────── */

function BlockTimeModal({
  providers,
  rooms,
  defaultDate,
  onClose,
  onSaved,
}: {
  providers: Provider[];
  rooms: Room[];
  defaultDate: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  const [target, setTarget] = useState<"provider" | "room">("provider");
  const [providerId, setProviderId] = useState(providers[0]?.id ?? "");
  const [roomId, setRoomId] = useState(rooms[0]?.id ?? "");
  const [start, setStart] = useState(`${defaultDate}T09:00`);
  const [end, setEnd] = useState(`${defaultDate}T10:00`);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const startIso = localInputToIso(start);
  const endIso = localInputToIso(end);
  const badRange = !!startIso && !!endIso && new Date(endIso).getTime() <= new Date(startIso).getTime();
  const targetId = target === "provider" ? providerId : roomId;
  const canSave = !!targetId && !!startIso && !!endIso && !badRange;

  const handleSave = async () => {
    if (!startIso || !endIso || !targetId || badRange) return;
    setSaving(true);
    try {
      const r = await api.post<{ overlapping?: number }>("/blocks", {
        provider_id: target === "provider" ? providerId : null,
        room_id: target === "room" ? roomId : null,
        starts_at: startIso,
        ends_at: endIso,
        reason: reason.trim() || null,
      });
      // It no longer refuses a window with visits in it, so say what it did
      // rather than letting her wonder whether those visits were touched.
      const n = Number(r?.overlapping ?? 0);
      toast.success("Time blocked", n > 0
        ? `${n} appointment${n === 1 ? "" : "s"} already in that window stay exactly where ${n === 1 ? "it is" : "they are"} - nothing new can be booked on top.`
        : undefined);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to block that time");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Block time">
      <div className="space-y-4">
        <div>
          <Label required>What's blocked</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              value={target}
              onChange={setTarget}
              options={[
                { value: "provider", label: "Provider" },
                /* "if we can keep the option and only block the chair not the
                   room is much better" - it always blocked the one chair, but
                   calling it Room read as the whole sunroom, so she blocked
                   the provider instead and then nothing could be booked
                   anywhere. Her word, everywhere else in the system, is seat. */
                { value: "room", label: "Seat" },
              ]}
            />
            {target === "provider" ? (
              <Select
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
                className="w-auto min-w-[12rem] flex-1"
              >
                <option value="" disabled>Choose a provider…</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            ) : (
              <Select
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                className="w-auto min-w-[12rem] flex-1"
              >
                <option value="" disabled>Choose a seat…</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </Select>
            )}
          </div>
          {/* Which one of these she picks decides whether a single chair or
              the whole of somebody's day comes off the board. */}
          <p className="mt-1.5 text-xs text-zinc-500">
            {target === "provider"
              ? "Blocks that person everywhere - no chair, no video, no phone, for as long as it lasts."
              : "Blocks that one chair. Everybody can still work in the others."}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label required>Starts</Label>
            {/* The end travels with the start, keeping the same length.
                Moving the start to the 21st used to leave the end on the
                19th, which greyed out "Block time" with nothing saying why -
                read from the desk as "it won't save". */}
            <DateTimePicker
              value={start}
              onChange={(next) => {
                const prevStart = new Date(start).getTime();
                const nextStart = new Date(next).getTime();
                const prevEnd = new Date(end).getTime();
                setStart(next);
                if (Number.isFinite(prevStart) && Number.isFinite(nextStart) && Number.isFinite(prevEnd)) {
                  const span = Math.max(prevEnd - prevStart, 0);
                  if (nextStart + span > prevEnd || nextStart >= prevEnd) {
                    const moved = new Date(nextStart + (span || 60 * 60_000));
                    setEnd(`${moved.getFullYear()}-${String(moved.getMonth() + 1).padStart(2, "0")}-${String(moved.getDate()).padStart(2, "0")}T${String(moved.getHours()).padStart(2, "0")}:${String(moved.getMinutes()).padStart(2, "0")}`);
                  }
                }
              }}
              placeholder="Pick a start"
            />
          </div>
          <div>
            <Label required>Ends</Label>
            <DateTimePicker value={end} onChange={setEnd} placeholder="Pick an end" />
          </div>
        </div>
        {badRange && <p className="text-xs text-rose-400">The end has to come after the start.</p>}
        {/* "when we try to block the time it won't save even if I try days" -
            a block CAN span days, it just meant setting both ends by hand. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-zinc-500">Block:</span>
          {([
            ["The rest of today", () => { const d = start.slice(0, 10); setStart(`${d}T${start.slice(11) || "09:00"}`); setEnd(`${d}T23:59`); }],
            ["All day", () => { const d = start.slice(0, 10); setStart(`${d}T00:00`); setEnd(`${d}T23:59`); }],
            ["3 days", () => { const d = new Date(`${start.slice(0, 10)}T00:00`); const e = new Date(d); e.setDate(e.getDate() + 2);
                               setStart(`${start.slice(0, 10)}T00:00`); setEnd(`${e.toISOString().slice(0, 10)}T23:59`); }],
            ["A week", () => { const d = new Date(`${start.slice(0, 10)}T00:00`); const e = new Date(d); e.setDate(e.getDate() + 6);
                               setStart(`${start.slice(0, 10)}T00:00`); setEnd(`${e.toISOString().slice(0, 10)}T23:59`); }],
          ] as const).map(([label, fn]) => (
            <button
              key={label}
              type="button"
              onClick={fn}
              className="rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
            >
              {label}
            </button>
          ))}
        </div>

        <div>
          <Label>Reason</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Lunch, laser service, staff meeting…"
          />
        </div>

        <p className="text-xs text-zinc-500">
          Blocked time is held off the booking engine — nobody can book into it online or from this screen.
        </p>
      </div>

      <ModalFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" loading={saving} disabled={!canSave} onClick={() => void handleSave()}>
          Block time
        </Button>
      </ModalFooter>
    </Modal>
  );
}
